import React from 'react';
import OrderDesign from '../../OrderDesign';
import {
  MOBILE_HAND_CAROUSEL_BOTTOM_PX,
  MOBILE_HAND_CAROUSEL_LEFT_PX,
  MOBILE_HAND_CAROUSEL_AVAILABLE_WIDTH_PX,
} from './mobileBattleLayoutOffsets';
import {
  MOBILE_HAND_CARD_BASE_HEIGHT,
  MOBILE_HAND_CARD_BASE_WIDTH,
  MOBILE_HAND_CARD_SCALE,
  computeMobileHandCarouselLayout,
} from './mobileHandCarouselLayout';
import {
  DUPLICATE_TARGET_IMAGE_PATH,
  DUPLICATE_TARGET_CENTERED_IMAGE_STYLE,
  DUPLICATE_HAND_OVERLAY_MATCH_ORDER_DESIGN_TRANSFORM,
} from '../../config/duplicateStrategemAnimation';

/**
 * Mobile Order Carousel — HUD lane with inner pad; hand spreads pad → drone edge.
 */
export const MobileOrderCarousel = ({
  you,
  yourTurn,
  hoveredIndex,
  setHoveredIndex,
  selectedOrderInstanceId,
  gameState,
  handleTouchDragStart,
  handleTouchDragMove,
  handleTouchDragEnd,
  handleCardTouch,
  playOrder,
  blockedOrderIds = [],
  isDisorientationHandLocked = false,
  needsMulligan = false,
  hasWeaponAttachTargetAvailable = true,
  duplicateHandPulseUntil = {},
  tacticalLoadoutHiddenHandInstanceIds = null,
}) => {
  const validHand = (you.hand || []).filter(order => {
    if (order === null || order === undefined) return false;
    if (!order.instanceId) return false;
    const inCompleted = you.completedOrders?.some(co => co && co.instanceId === order.instanceId);
    if (inCompleted) return false;
    const inDeck = you.deck?.some(d => d && d.instanceId === order.instanceId);
    if (inDeck) return false;
    return true;
  });

  const handToDisplay = needsMulligan ? [] : validHand;

  if (handToDisplay.length === 0) return null;

  const cardCount = handToDisplay.length;
  const {
    scaledCardWidth,
    scaledCardHeight,
    cardSpacing,
    startOffset,
  } = computeMobileHandCarouselLayout(cardCount);

  return (
    <div
      className="order-carousel-mobile-wrapper"
      style={{
        position: 'absolute',
        bottom: `${MOBILE_HAND_CAROUSEL_BOTTOM_PX}px`,
        left: `${MOBILE_HAND_CAROUSEL_LEFT_PX}px`,
        width: `${MOBILE_HAND_CAROUSEL_AVAILABLE_WIDTH_PX}px`,
        height: `${scaledCardHeight}px`,
        zIndex: 1003,
        overflow: 'visible',
      }}
    >
      {handToDisplay.map((order, idx) => {
        if (!order || !order.instanceId) return null;

        const isHovered = idx === hoveredIndex;
        const isBlockedByOrderRule = Array.isArray(blockedOrderIds) && blockedOrderIds.includes(order.id);
        const isBlocked = isBlockedByOrderRule || isDisorientationHandLocked;
        const orderCost =
          order.hasInstanceModifications && order.instanceCost !== undefined
            ? order.instanceCost
            : (order.cost || 0);
        const isWeaponWithoutAttachTarget =
          order.type === 'Weapon' && !hasWeaponAttachTargetAvailable;
        const intelNum = Number(you.intelligence);
        const costNum = Number(orderCost);
        const isPlayable =
          !isBlocked &&
          yourTurn &&
          Number.isFinite(intelNum) &&
          Number.isFinite(costNum) &&
          intelNum >= costNum &&
          !isWeaponWithoutAttachTarget;

        const cardLeft = startOffset + idx * cardSpacing;
        const dupSid = order.instanceId != null ? String(order.instanceId) : '';
        const showDupOverlay =
          order.duplicatedFrom &&
          dupSid &&
          typeof duplicateHandPulseUntil[dupSid] === 'number' &&
          duplicateHandPulseUntil[dupSid] > Date.now();

        const hideForTacticalLoadoutFx =
          tacticalLoadoutHiddenHandInstanceIds &&
          tacticalLoadoutHiddenHandInstanceIds.has(String(order.instanceId));

        return (
          <div
            key={`hand-${idx}-${order.id}`}
            data-instance-id={order.instanceId}
            data-order-id={order.id}
            className={`carousel-order-card-mobile${isHovered ? ' expanded' : ''}`}
            onTouchStart={(e) => {
              if (isBlocked || !yourTurn) return;
              setHoveredIndex(idx);
              handleTouchDragStart(e, order);
            }}
            onTouchMove={(e) => {
              if (!yourTurn) return;
              handleTouchDragMove(e);
            }}
            onTouchEnd={(e) => {
              if (isBlocked || !yourTurn) return;
              const { wasDragging, order: dragOrder, zone } = handleTouchDragEnd(e);
              setHoveredIndex(null);

              if (wasDragging) {
                e.preventDefault();
                e.stopPropagation();
                if (dragOrder && zone) {
                  const isDeployableToZone = dragOrder.type !== 'Drone' &&
                    dragOrder.type !== 'Strategem' &&
                    dragOrder.type !== 'Weapon';
                  if (isDeployableToZone) {
                    playOrder(dragOrder.id, zone, null, dragOrder.instanceId);
                  } else {
                    handleCardTouch(dragOrder, e);
                  }
                }
              } else {
                e.preventDefault();
                e.stopPropagation();
                handleCardTouch(order, e);
              }
            }}
            onClick={(e) => {
              if (isBlocked) return;
              e.preventDefault();
              e.stopPropagation();
              handleCardTouch(order, e);
            }}
            style={{
              position: 'absolute',
              left: `${cardLeft}px`,
              bottom: 0,
              width: `${scaledCardWidth}px`,
              height: `${scaledCardHeight}px`,
              cursor: isBlocked ? 'not-allowed' : 'pointer',
              pointerEvents: hideForTacticalLoadoutFx || isBlocked ? 'none' : undefined,
              opacity: hideForTacticalLoadoutFx ? 0 : isBlocked ? 0.6 : 1,
              visibility: hideForTacticalLoadoutFx ? 'hidden' : 'visible',
              zIndex: isHovered ? 100 : idx,
            }}
          >
            <div
              style={{
                width: `${MOBILE_HAND_CARD_BASE_WIDTH}px`,
                height: `${MOBILE_HAND_CARD_BASE_HEIGHT}px`,
                transform: `scale(${MOBILE_HAND_CARD_SCALE})`,
                transformOrigin: 'bottom left',
                position: 'absolute',
                bottom: 0,
                left: 0,
              }}
            >
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {showDupOverlay ? (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      zIndex: 150,
                      transform: DUPLICATE_HAND_OVERLAY_MATCH_ORDER_DESIGN_TRANSFORM,
                    }}
                  >
                    <img
                      src={DUPLICATE_TARGET_IMAGE_PATH}
                      alt=""
                      draggable={false}
                      style={DUPLICATE_TARGET_CENTERED_IMAGE_STYLE}
                    />
                  </div>
                ) : null}
                <OrderDesign
                  order={order}
                  count={0}
                  minimal={true}
                  scale={0.5}
                  isOOR={!isPlayable && yourTurn}
                  isSelected={selectedOrderInstanceId === order.instanceId}
                  isPlayable={isPlayable}
                  gameState={gameState}
                  isHiddenDuringAnimation={hideForTacticalLoadoutFx}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MobileOrderCarousel;
